import type { MessageKey } from "@colorglo/i18n";
import {
  type Icon,
  NavCalendar,
  NavNotifications,
  NavOverview,
  NavReports,
  NavShop,
  NavShopOrders,
  NavShopProducts,
  NavWorkshops,
  SettingsCalendar,
  SettingsHours,
  SettingsIntegrations,
  SettingsMobile,
  SettingsNotifications,
  SettingsProfile,
  SettingsReminders,
  SettingsRules,
  SettingsServices,
  SettingsStaff,
} from "@colorglo/ui/icons";

export interface NavItem {
  readonly to: string;
  readonly label: MessageKey;
  readonly icon: Icon;
}

export const MAIN: readonly NavItem[] = [
  { to: "/", label: "nav.calendar", icon: NavCalendar },
  { to: "/oversikt", label: "nav.overview", icon: NavOverview },
];

/**
 * The notifications page, shown only to someone who holds `staff_notification:read`.
 *
 * Apart from `MAIN` because it is permission-gated and that list is not — the two items
 * above are things every signed-in staff member has. Gated on the permission rather than on
 * a role, for the same reason the bell in the header is: HQ reads the network dashboard and
 * reaches a workshop's day by impersonating.
 */
export const NOTIFICATIONS: NavItem = {
  to: "/aviseringar",
  label: "bell.title",
  icon: NavNotifications,
};

/**
 * The HQ dashboard (P10), shown only to someone who has one.
 *
 * Gated on `me.network.countries` rather than on a role: a country manager fails every
 * network-scope permission check by design and still has the reports for their country, so
 * the role is the wrong question and the list of countries is the right one. See the note in
 * `apps/api/src/routes/me.ts`.
 */
export const NETWORK: NavItem = { to: "/natverk", label: "network.title", icon: NavWorkshops };

export const SHOP: NavItem = { to: "/butik", label: "shop.title", icon: NavShop };

export const SHOP_ORDERS: NavItem = {
  to: "/bestallningar",
  label: "shop.hq.title",
  icon: NavShopOrders,
};

export const SHOP_PRODUCTS: NavItem = {
  to: "/produkter",
  label: "shop.admin.title",
  icon: NavShopProducts,
};

export const SETTINGS_ROOT = "/installningar";

/**
 * The screens that belong to the signed-in person rather than to the workshop.
 *
 * Split out because the settings tabs are gated on `booking_policy:manage`,
 * and a technician who cannot edit the franchise's rules still owns their own bell. They see
 * this list; an owner sees it folded into `SETTINGS` below, in one place, in one order.
 */
export const PERSONAL_SETTINGS: readonly NavItem[] = [
  {
    to: `${SETTINGS_ROOT}/aviseringar`,
    label: "settings.nav.notifications",
    icon: SettingsNotifications,
  },
];

export const SETTINGS: readonly NavItem[] = [
  ...PERSONAL_SETTINGS,
  { to: `${SETTINGS_ROOT}/oppettider`, label: "settings.nav.hours", icon: SettingsHours },
  { to: `${SETTINGS_ROOT}/personal`, label: "settings.nav.staff", icon: SettingsStaff },
  { to: `${SETTINGS_ROOT}/tjanster`, label: "settings.nav.services", icon: SettingsServices },
  { to: `${SETTINGS_ROOT}/bokningsregler`, label: "settings.nav.rules", icon: SettingsRules },
  { to: `${SETTINGS_ROOT}/paminnelser`, label: "settings.nav.reminders", icon: SettingsReminders },
  { to: `${SETTINGS_ROOT}/rapporter`, label: "settings.nav.reports", icon: NavReports },
  { to: `${SETTINGS_ROOT}/mobil`, label: "settings.nav.mobile", icon: SettingsMobile },
  { to: `${SETTINGS_ROOT}/kalender`, label: "settings.nav.calendar", icon: SettingsCalendar },
  {
    to: `${SETTINGS_ROOT}/integrationer`,
    label: "settings.nav.integrations",
    icon: SettingsIntegrations,
  },
  { to: `${SETTINGS_ROOT}/profil`, label: "settings.nav.profile", icon: SettingsProfile },
];

export function settingsTabs(
  franchises: readonly { readonly permissions: readonly string[] }[],
): readonly NavItem[] {
  return franchises.some((franchise) => franchise.permissions.includes("booking_policy:manage"))
    ? SETTINGS
    : PERSONAL_SETTINGS;
}

/**
 * Where you are, as message keys, for the header breadcrumb.
 *
 * The same lists the sidebar renders from, so a section cannot be renamed in one place and
 * keep its old name in the other.
 */
export function crumbsFor(pathname: string): readonly MessageKey[] {
  if (pathname.startsWith(NETWORK.to)) return [NETWORK.label];
  if (pathname.startsWith(NOTIFICATIONS.to)) return [NOTIFICATIONS.label];
  if (pathname.startsWith(SHOP.to)) return [SHOP.label];
  if (pathname.startsWith(SHOP_ORDERS.to)) return [SHOP_ORDERS.label];
  if (pathname.startsWith(SHOP_PRODUCTS.to)) return [SHOP_PRODUCTS.label];

  if (pathname.startsWith(SETTINGS_ROOT)) {
    const leaf = SETTINGS.find((item) => item.to === pathname);
    return leaf === undefined ? ["settings.title"] : ["settings.title", leaf.label];
  }

  return [MAIN.find((item) => item.to === pathname)?.label ?? "nav.calendar"];
}
